// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import type { AppointmentTypeDefinition, AvailableTimeSlot } from '@polarissleep/scheduling';
import { findAvailableSlots, findEligibleProviders, rankProvidersByUtilization } from '@polarissleep/scheduling';
import { useCallback, useEffect, useState } from 'react';

const slotsCache = new Map<string, AvailableTimeSlot[]>();
const slotsInFlight = new Map<string, Promise<AvailableTimeSlot[]>>();

interface UseAvailableSlotsOptions {
  /** Pre-resolved appointment type definition (avoids redundant CodeSystem fetch) */
  appointmentType: AppointmentTypeDefinition | undefined;
  /** Optional practitioner filter; when provided slot loading is scoped to that staff member only */
  practitionerId?: string;
  /** Start of date range (ISO date string) */
  startDate: string | undefined;
  /** End of date range (ISO date string) */
  endDate: string | undefined;
}

/**
 * Hook that fetches available time slots for a given appointment type and date range.
 *
 * Identical to the patient-app version — no patient-context parameterization
 * is needed because slot availability is provider/schedule-scoped, not
 * patient-scoped. Accepts a pre-resolved AppointmentTypeDefinition to avoid
 * redundant CodeSystem fetches. Only fetches when all parameters are provided.
 * @param root0 - The options object
 * @param root0.appointmentType - The pre-resolved appointment type definition
 * @param root0.startDate - Start of the date range (ISO date string)
 * @param root0.endDate - End of the date range (ISO date string)
 * @returns Available slots, loading state, error, and refresh function
 */
export function useAvailableSlots({ appointmentType, practitionerId, startDate, endDate }: UseAvailableSlotsOptions): {
  slots: AvailableTimeSlot[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
} {
  const medplum = useMedplum();
  const [slots, setSlots] = useState<AvailableTimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const cacheKey =
    appointmentType?.coding.code && startDate && endDate
      ? `${appointmentType.coding.code}|${practitionerId ?? 'eligible'}|${startDate}|${endDate}`
      : undefined;

  const fetchSlots = useCallback(
    async (force = false) => {
      if (!appointmentType || !startDate || !endDate) {
        setSlots([]);
        setError(null);
        setLoading(false);
        return;
      }

      if (cacheKey && !force) {
        const cached = slotsCache.get(cacheKey);
        if (cached) {
          setSlots(cached);
          setError(null);
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      setError(null);

      try {
        let request = cacheKey ? slotsInFlight.get(cacheKey) : undefined;
        if (!request || force) {
          request = (async () => {
            const providerNames: Record<string, string> = {};
            let practitionerIds: string[] = [];

            if (practitionerId) {
              const practitioner = await medplum.readResource('Practitioner', practitionerId);
              practitionerIds = [practitionerId];
              providerNames[practitionerId] = practitioner.name?.[0]
                ? formatHumanName(practitioner.name[0])
                : 'Provider';
            } else {
              const providers = await findEligibleProviders(medplum, appointmentType);
              if (providers.length === 0) {
                return [];
              }

              practitionerIds = providers.map((p) => p.practitioner.id as string);
              for (const p of providers) {
                if (p.practitioner.id) {
                  providerNames[p.practitioner.id] = p.practitioner.name?.[0]
                    ? formatHumanName(p.practitioner.name[0])
                    : 'Provider';
                }
              }
            }

            const rankedIds = await rankProvidersByUtilization(medplum, practitionerIds, startDate, endDate);
            const availableSlots = await findAvailableSlots(medplum, {
              practitionerIds: rankedIds,
              startDate,
              endDate,
              appointmentType,
              providerNames,
              reconcileMissingSlots: true,
            });

            const seen = new Set<string>();
            return availableSlots.filter((slot) => {
              if (seen.has(slot.start)) {
                return false;
              }
              seen.add(slot.start);
              return true;
            });
          })();
          if (cacheKey) {
            slotsInFlight.set(cacheKey, request);
          }
        }

        const dedupedSlots = await request;
        if (cacheKey) {
          slotsCache.set(cacheKey, dedupedSlots);
        }
        setSlots(dedupedSlots);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load available slots'));
      } finally {
        if (cacheKey) {
          slotsInFlight.delete(cacheKey);
        }
        setLoading(false);
      }
    },
    [cacheKey, medplum, appointmentType, practitionerId, startDate, endDate]
  );

  useEffect(() => {
    fetchSlots().catch(console.error);
  }, [fetchSlots]);

  return { slots, loading, error, refresh: () => fetchSlots(true) };
}
